import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { toast } from 'sonner';
import { DollarSign, Store, Truck, Check, Clock, AlertTriangle, Play, CreditCard, Banknote } from 'lucide-react';
import api from '../../services/api';

const GigSettlements = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [summary, setSummary] = useState(null);
  const [settlements, setSettlements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  useEffect(() => { loadData(); }, [activeTab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'summary') {
        const res = await api.get('/gig/admin/settlements/summary', { params: { period: 'current_week' } });
        setSummary(res.data);
      } else if (activeTab === 'stores') {
        const res = await api.get('/gig/admin/settlements/history', { params: { settlement_type: 'store' } });
        setSettlements(res.data.settlements || []);
      } else if (activeTab === 'drivers') {
        const res = await api.get('/gig/admin/settlements/history', { params: { settlement_type: 'driver' } });
        setSettlements(res.data.settlements || []);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const runCutoff = async () => {
    if (!confirm('¿Ejecutar corte semanal? Esto genera las liquidaciones de tiendas y repartidores.')) return;
    setRunning(true);
    try {
      const res = await api.post('/gig/admin/settlements/run-cutoff');
      toast.success(`Corte ejecutado: ${res.data.stores} tiendas, ${res.data.drivers} repartidores`);
      loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al ejecutar corte');
    } finally { setRunning(false); }
  };

  const markPaid = async (id) => {
    try {
      await api.put(`/gig/admin/settlements/${id}/mark-paid`);
      toast.success('Marcado como pagado');
      loadData();
    } catch (e) { toast.error('Error'); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : '—';
  const formatMoney = (n) => `$${(n || 0).toFixed(2)}`;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight">Liquidaciones</h1>
            <p className="text-slate-600 mt-2">Cortes semanales de tiendas y repartidores</p>
          </div>
          <button onClick={runCutoff} disabled={running}
            className="flex items-center gap-2 px-5 py-3 bg-teal-700 text-white rounded-lg hover:bg-teal-800 font-medium transition disabled:opacity-50">
            <Play className="w-4 h-4" />
            {running ? 'Ejecutando...' : 'Ejecutar corte semanal'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2">
          {[
            { key: 'summary', label: 'Resumen', icon: DollarSign },
            { key: 'stores', label: 'Tiendas', icon: Store },
            { key: 'drivers', label: 'Repartidores', icon: Truck },
          ].map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition ${
                activeTab === tab.key ? 'bg-teal-700 text-white' : 'bg-white text-slate-600 border border-slate-200'
              }`}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-teal-700"></div>
          </div>
        ) : activeTab === 'summary' && summary ? (
          /* SUMMARY TAB */
          <div className="space-y-6">
            <p className="text-sm text-slate-500">Período: {summary.period?.start ? `${formatDate(summary.period.start)} - ${formatDate(summary.period.end)}` : 'Semana actual'}</p>

            {/* Revenue Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-sm text-slate-500 mb-1">Ingresos por markup (10%)</p>
                <p className="text-2xl font-bold text-emerald-600">{formatMoney(summary.platform_revenue?.markup)}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-sm text-slate-500 mb-1">Cargos por servicio</p>
                <p className="text-2xl font-bold text-emerald-600">{formatMoney(summary.platform_revenue?.service_fees)}</p>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="text-sm text-slate-500 mb-1">Comisiones de tiendas</p>
                <p className="text-2xl font-bold text-emerald-600">{formatMoney(summary.platform_revenue?.store_commissions)}</p>
              </div>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <p className="text-sm text-emerald-700 mb-1">Ingreso total de la plataforma</p>
              <p className="text-4xl font-black text-emerald-700">{formatMoney(summary.platform_revenue?.total)}</p>
              <p className="text-xs text-emerald-600 mt-2">{summary.orders?.total_orders || 0} pedidos en el período</p>
            </div>

            {/* Net Settlement */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><Truck className="w-5 h-5 text-blue-600" /> Balance Repartidores</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Repartidores deben a plataforma</span>
                    <span className="font-medium">{formatMoney(summary.net_settlement?.drivers_owe_platform)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Plataforma debe a repartidores</span>
                    <span className="font-medium">{formatMoney(summary.net_settlement?.platform_owes_drivers)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-slate-100">
                    <span className="font-bold">Balance neto</span>
                    <span className={`font-bold ${summary.net_settlement?.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatMoney(summary.net_settlement?.net)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2"><Store className="w-5 h-5 text-amber-600" /> Comisiones Tiendas</h3>
                <p className="text-3xl font-bold text-amber-600">{formatMoney(summary.store_commissions?.total_owed_by_stores)}</p>
                <p className="text-xs text-slate-500 mt-2">Pendiente de cobro en ventas en efectivo</p>
              </div>
            </div>
          </div>
        ) : activeTab !== 'summary' ? (
          /* STORES / DRIVERS TABS */
          <div className="space-y-3">
            {settlements.length === 0 ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
                <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No hay liquidaciones. Ejecuta el corte semanal primero.</p>
              </div>
            ) : settlements.map(s => (
              <div key={s.id} className="bg-white rounded-xl border border-slate-200 p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-slate-900">{s.entity_name}</h3>
                    <p className="text-xs text-slate-500">{s.period}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                      s.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {s.status === 'paid' ? 'Pagado' : 'Pendiente'}
                    </span>
                  </div>
                </div>

                {s.type === 'store' ? (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div>
                      <p className="text-slate-500">Ventas totales</p>
                      <p className="font-bold">{formatMoney(s.total_sales)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 flex items-center gap-1"><Banknote className="w-3 h-3" /> Efectivo</p>
                      <p className="font-bold">{formatMoney(s.cash_sales)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 flex items-center gap-1"><CreditCard className="w-3 h-3" /> Tarjeta</p>
                      <p className="font-bold">{formatMoney(s.card_sales)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Comisión (10%)</p>
                      <p className="font-bold text-amber-600">{formatMoney(s.commission_amount)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Depositar</p>
                      <p className="font-bold text-emerald-600">{formatMoney(s.deposit_amount)}</p>
                      {s.store_owes > 0 && (
                        <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                          <AlertTriangle className="w-3 h-3" /> Tienda debe {formatMoney(s.store_owes)}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-sm">
                    <div>
                      <p className="text-slate-500">Ganancias</p>
                      <p className="font-bold">{formatMoney(s.gross_earnings)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Deuda plataforma</p>
                      <p className="font-bold text-amber-600">{formatMoney(s.platform_debt)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Neto</p>
                      <p className={`font-bold ${s.net_settlement >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatMoney(s.net_settlement)}
                      </p>
                      {s.net_settlement < 0 && (
                        <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                          <AlertTriangle className="w-3 h-3" /> Debe a enLocal
                        </p>
                      )}
                    </div>
                    <div>
                      <p className="text-slate-500">Entregas</p>
                      <p className="font-bold">{s.deliveries}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">CLABE</p>
                      <p className="font-mono text-xs">{s.bank_clabe ? `****${s.bank_clabe.slice(-4)}` : 'N/A'}</p>
                      <p className="text-xs text-slate-400">{s.bank_name || ''}</p>
                    </div>
                  </div>
                )}

                {s.status === 'pending' && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex justify-end">
                    <button onClick={() => markPaid(s.id)}
                      className="flex items-center gap-1 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition">
                      <Check className="w-4 h-4" /> Marcar como pagado
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </DashboardLayout>
  );
};

export default GigSettlements;
